/**
 * Text component — shared types and Zod prop schema.
 *
 * Renders styled text using the design system's typography and colour elements.
 * Variant names map 1:1 to the typography element keys; colour names map 1:1
 * to the colors.text semantic palette.
 *
 * Source of truth: Pangea Galapagos UI Design System — Beta
 * https://www.figma.com/design/Orc6uUodQaoXep59CGaUNw
 */

import { z } from 'zod';
import { colors } from '../../elements/colors';
import { UNCARDED_TEXT_INSET } from '../../elements/spacing';
import { STAT_FIGURE_FONT_FAMILY, typography } from '../../elements/typography';

export const TextVariant = z.enum([
  'displayBold',
  'displayLargeBold',
  'displayJumboBold',
  'h1Bold',
  'h1Bold28',
  'h1Bold28Italic',
  'h1Bold48',
  'h1Regular',
  'statFigure',
  'h2Bold',
  'b1Bold',
  'b1Regular',
  'b1BoldItalic',
  'b2Bold',
  'b2Regular',
  'b2Regular24',
  'b3Regular',
  'b3Bold',
  'c1Regular',
  'c1Medium',
  'eyebrow',
  'c2Regular',
  'button1',
  'button2',
  'button3',
]);
export type TextVariant = z.infer<typeof TextVariant>;

export const TextColor = z.enum([
  'primary',
  'secondary',
  'tertiary',
  'disabled',
  'inverted',
  'alert',
  'success',
  'highlight',
  'highlightInverted',
  'insight',
  'global',
]);
export type TextColor = z.infer<typeof TextColor>;

export const TextAlign = z.enum(['left', 'center', 'right']);
export type TextAlign = z.infer<typeof TextAlign>;

export const TextRole = z.enum(['text', 'header', 'alert']);
export type TextRole = z.infer<typeof TextRole>;

/**
 * Role-named horizontal inset for text that does not sit in a card.
 *
 * `uncarded` lands the text on the wide screen gutter while the host screen keeps
 * the card gutter: the extra distance is internal padding owned by the text
 * component (ADR-091 Rule 7), never a wrapper's padding. A build that predates
 * the option drops it and renders the text on the card gutter.
 */
export const TextInset = z.enum(['uncarded']);
export type TextInset = z.infer<typeof TextInset>;

/** Resolve a text inset to its internal horizontal padding (0 when absent). */
export function textInsetPadding(inset: TextInset | undefined): number {
  return inset === 'uncarded' ? UNCARDED_TEXT_INSET : 0;
}

/**
 * One inline run of a rich text block. `bold` renders the run at the variant's
 * bold weight while every other run inherits the base variant, so a single
 * paragraph can carry an emphasised span (e.g. a phone number) and still wrap
 * as one flowing block.
 */
export const TextSegment = z.object({
  text: z.string(),
  bold: z.boolean().optional(),
  /**
   * Draw the run in the stat-figure face (figures and currency symbols only —
   * a letter would fall back to the platform font). Builds that predate the
   * field ignore it and draw the run in the base face.
   */
  figureFace: z.boolean().optional(),
});
export type TextSegment = z.infer<typeof TextSegment>;

/** The font overrides one rich run takes over its block's variant. */
export function textSegmentFontStyle(segment: TextSegment): {
  fontWeight?: (typeof typography)['b1Bold']['fontWeight'];
  fontFamily?: string;
} {
  if (segment.figureFace) {
    return { fontFamily: STAT_FIGURE_FONT_FAMILY, fontWeight: typography.statFigure.fontWeight };
  }
  return segment.bold ? { fontWeight: typography.b1Bold.fontWeight } : {};
}

export const textPropsSchema = z.object({
  variant: TextVariant.default('b1Regular'),
  color: TextColor.default('primary'),
  align: TextAlign.default('left'),
  role: TextRole.default('text'),
  value: z.string(),
  /**
   * Optional inline rich runs. When present the runs render as a single flowing
   * text block (each `bold` run at the variant's bold weight), and `value` is the
   * plain-text fallback / accessibility string. Omit for plain single-weight text.
   */
  segments: z.array(TextSegment).optional(),
  testID: z.string().optional(),
  accessibilityLabel: z.string().optional(),
  // Controls whether the text participates in the accessibility tree. Hosts
  // use this for visually-timed content whose hidden descendants must not be
  // announced before they are visible.
  accessible: z.boolean().optional(),
  // Clamp to N lines with a trailing ellipsis (maps to RN Text numberOfLines).
  numberOfLines: z.number().int().positive().optional(),
  /** Optional role-named horizontal inset; see `TextInset`. */
  inset: TextInset.optional(),
});

export type TextProps = z.infer<typeof textPropsSchema>;

/** Resolve a TextVariant to its typography style. */
export const typographyForVariant: Record<
  TextVariant,
  (typeof typography)[keyof typeof typography]
> = {
  displayBold: typography.displayBold,
  displayLargeBold: typography.displayLargeBold,
  displayJumboBold: typography.displayJumboBold,
  h1Bold: typography.h1Bold,
  h1Bold28: typography.h1Bold28,
  h1Bold28Italic: typography.h1Bold28Italic,
  h1Bold48: typography.h1Bold48,
  h1Regular: typography.h1Regular,
  statFigure: typography.statFigure,
  h2Bold: typography.h2Bold,
  b1Bold: typography.b1Bold,
  b1Regular: typography.b1Regular,
  b1BoldItalic: typography.b1BoldItalic,
  b2Bold: typography.b2Bold,
  b2Regular: typography.b2Regular,
  b2Regular24: typography.b2Regular24,
  b3Regular: typography.b3Regular,
  b3Bold: typography.b3Bold,
  c1Regular: typography.c1Regular,
  c1Medium: typography.c1Medium,
  eyebrow: typography.eyebrow,
  c2Regular: typography.c2Regular,
  button1: typography.button1,
  button2: typography.button2,
  button3: typography.button3,
};

/** Resolve a TextColor to its hex value. */
export const colorForName: Record<TextColor, string> = {
  primary: colors.text.primary,
  secondary: colors.text.secondary,
  tertiary: colors.text.tertiary,
  disabled: colors.text.disabled,
  inverted: colors.text.inverted,
  alert: colors.text.alert,
  success: colors.text.success,
  highlight: colors.text.highlight,
  highlightInverted: colors.text.highlightInverted,
  insight: colors.text.insight,
  global: colors.text.global,
};

/**
 * Bind the last two words of a string's final line with a non-breaking space so
 * a single short word never wraps alone onto its own line (a typographic
 * orphan). Only words separated by horizontal whitespace are joined, so authored
 * line breaks (`\n`) are preserved and never collapsed. A single-word string is
 * returned unchanged. Screen readers treat the non-breaking space as a normal
 * space, and the default testing-library whitespace normaliser collapses it too,
 * so text queries keep matching.
 */
// Orphan prevention is a shared string transform; re-exported here so `Text`
// consumers keep importing it from the component.
export { preventOrphan } from '@galapagos/shared/src/text/preventOrphan';

export const DEFAULT_HEADING_ARIA_LEVEL = 2;

export function ariaAttributesForRole(
  role: TextRole,
): { role: 'heading'; 'aria-level': number } | { role: 'alert' } | Record<string, never> {
  if (role === 'header') {
    return { role: 'heading', 'aria-level': DEFAULT_HEADING_ARIA_LEVEL };
  }
  if (role === 'alert') {
    return { role: 'alert' };
  }
  return {};
}
